import { useRef, useState, useEffect } from 'react';
import type { Specialization } from '../../types/signup';
import '../../styles/signup.css';

interface AnimatedCard3DProps {
  clinicName?: string;
  specializationId?: number;
  specializations: Specialization[];
}

export const AnimatedCard3D = ({
  clinicName = ' ',
  specializationId,
  specializations,
}: AnimatedCard3DProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });

  // Find the current specialization from the list
  const currentSpecialization = specializations.find((s) => s.id === specializationId);
  // Default to first specialization if none selected
  const displaySpecialization = currentSpecialization || specializations[0] || { icon: 'visibility', name: 'Loading...' };

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = ((y - centerY) / centerY) * -10;
      const rotateY = ((x - centerX) / centerX) * 10;
      
      requestAnimationFrame(() => {
        card.style.setProperty('transform', 
          `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
        );
        
        // Update light reflection position
        const mouseXPercent = (x / rect.width) * 100;
        const mouseYPercent = (y / rect.height) * 100;
        card.style.setProperty('--mouse-x', `${mouseXPercent}%`);
        card.style.setProperty('--mouse-y', `${mouseYPercent}%`);
        
        setMousePosition({ x: mouseXPercent, y: mouseYPercent });
      });
    };

    const handleMouseLeave = () => {
      requestAnimationFrame(() => {
        card.style.setProperty('transform', 'perspective(1000px) rotateX(0deg) rotateY(0deg)');
      });
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div className="card-3d-wrapper">
      <div 
        ref={cardRef}
        className="digital-card-frost card-3d w-[520px] h-[300px] rounded-2xl p-10 flex flex-col justify-between transition-smooth hover-lift"
        style={{
          '--mouse-x': `${mousePosition.x}%`,
          '--mouse-y': `${mousePosition.y}%`,
        } as React.CSSProperties}
      >
        <div className="refracted-border rounded-2xl"></div>
        
        {/* Top Section */}
        <div className="flex justify-between items-start relative z-10">
          <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center shadow-xl shadow-primary/20">
            <span className="material-icons-round text-white text-3xl">{displaySpecialization.icon}</span>
          </div>
          <div className="w-10 h-10 rounded-full border border-[#101822]/5 flex items-center justify-center bg-white/20 backdrop-blur-sm">
            <span className="material-symbols-outlined text-[#101822]/40 text-lg">contactless</span>
          </div>
        </div>

        {/* Middle Section */}
        <div className="space-y-1.5 relative z-10">
          <h2 className="text-4xl font-extrabold text-[#101822] tracking-tight">
            {clinicName}
          </h2>
          <p className="text-[#101822]/50 text-xs font-bold tracking-[0.2em] uppercase">
            Premium Healthcare Excellence
          </p>
        </div>

        {/* Bottom Section */}
        <div className="flex justify-between items-end border-t border-[#101822]/5 pt-6 relative z-10">
          <div className="text-[10px] text-[#101822]/30 font-mono tracking-widest uppercase">
            Member ID: 8829-CNCT-2024
          </div>
          <div className="flex gap-2 items-center bg-white/40 px-3 py-1 rounded-full border border-white/60">
            <div className="w-2 h-2 rounded-full bg-primary pulse-glow"></div>
            <span className="text-[10px] text-primary font-bold uppercase tracking-wider">
              Verified Identity
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
